import type { Project } from '../domain/schemas';
import { downloadText } from '../lib/download';
import { exportUnsavedProject, type OmittedFile, type UnsavedBackup } from '../storage/importExport';
import type { ProjectRepository } from '../storage/projectRepository';

/**
 * Edits that could not be saved when their project was closed (the final save failed,
 * for example because storage is full or the project was deleted elsewhere). They are
 * kept in memory and offered as a backup file; nothing is dropped silently.
 */
export interface UnsavedWork {
  project: Project;
  error: string;
}

export type BackupSummary = Pick<UnsavedBackup, 'fileName' | 'omitted' | 'complete'>;

/**
 * Builds and downloads an importable backup of unsaved work. Resolves only after the file
 * was handed to the browser; rejects (StorageError) when no importable backup could be
 * produced, in which case nothing is downloaded.
 */
export async function downloadUnsavedCopy(repo: ProjectRepository, project: Project): Promise<BackupSummary> {
  const backup = await exportUnsavedProject(repo, project);
  downloadText(backup.fileName, backup.text);
  return { fileName: backup.fileName, omitted: backup.omitted, complete: backup.complete };
}

const REASON: Record<OmittedFile['reason'], string> = {
  missing: 'no longer stored',
  damaged: 'damaged',
  unreadable: 'storage could not be read',
};

/** What to tell the user about a downloaded backup: complete, or exactly what it lacks. */
export function describeBackup(summary: BackupSummary): { tone: 'success' | 'warning'; title: string; details: string[] } {
  if (summary.complete) {
    return {
      tone: 'success',
      title: `Backup downloaded: “${summary.fileName}”. It contains all your changes and files.`,
      details: ['To restore it, import the file on the Projects page.'],
    };
  }
  const n = summary.omitted.length;
  return {
    tone: 'warning',
    title: `Backup downloaded: “${summary.fileName}”, but ${n} file${n === 1 ? '' : 's'} could not be included.`,
    details: [
      ...summary.omitted.map((o) => `${o.name} (${o.kind}, ${REASON[o.reason]})`),
      'The clips, samples, images and their animations or reactions that used these files were removed from the backup; everything else is included. After importing it, add those files again if you still have them.',
    ],
  };
}
